import {Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {
  colorStringToPaletteColor,
  tryParseColor,
} from '../../../../../libs/shared/ui/src/styles/colorHelpers';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {lightTheme} from '../../../../../libs/shared/ui/src/theme/light/lightTheme';
import {PaletteColorPicker} from '../../../../components/common/color/paletteColorPicker';
import {PaletteColorPickerItem} from '../../../../components/common/color/paletteColorPickerItem';
import {ChannelFrontChatModel} from '../../../../models/api/channelModel';
import {SettingsFieldLine} from '../../../common/settingsFieldLine';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 * Props.
 */

interface SettingsChatWidgetColorProps extends SettingsCommonChannelFieldProps<ChannelFrontChatModel> {}

/*
 * Style.
 */

const StyledCustomColorTextBox = styled(TextBox)`
  > div:first-child {
    width: 78px;
  }
`;

const StyledColorPickerDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-items: flex-start;
  margin-top: -10px;
  width: 100%;
  grid-template-columns: auto 1fr;
  justify-items: flex-start;
`;

const StyledPaletteColorPicker = styled(PaletteColorPicker)`
  margin-top: -16px;
`;

const StyledCustomColorDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto 1fr;
  grid-column-gap: 10px;
  justify-items: flex-start;
  padding-top: 16px;
  padding-left: 15px;
  margin-left: 12px;
  position: relative;
  min-height: 79px;

  &::before {
    content: '';
    left: 0;
    top: 50%;
    margin-top: -14px;
    height: 28px;
    background-color: #e6e6e6;
    width: 1px;
    position: absolute;
  }
`;

/*
 * Component.
 */

const colorItems = [
  PaletteColorsEnum.PINK,
  PaletteColorsEnum.RED,
  PaletteColorsEnum.ORANGE,
  PaletteColorsEnum.YELLOW,
  PaletteColorsEnum.GREEN,
  PaletteColorsEnum.TEAL,
  PaletteColorsEnum.BLUE,
  PaletteColorsEnum.PURPLE,
] as const;

export const SettingsChatWidgetColor: FC<SettingsChatWidgetColorProps> = (props) => {
  const [isCustomColorSelected, setIsCustomColorSelected] = useState<boolean>();
  const paletteColor = useMemo(() => {
    if (isCustomColorSelected) {
      return undefined;
    }

    return colorStringToPaletteColor(props.edited.preferences.widgetColor, lightTheme, colorItems);
  }, [isCustomColorSelected, props.edited.preferences.widgetColor]);

  return (
    <SettingsFieldLine
      title={<Trans id="settingschannel-widgetcolor-title">Chat widget color</Trans>}
      subtitle={
        <Trans id="settingschannel-widgetcolor-subtitle">Choose a theme cololr for your chat widget</Trans>
      }
      content={
        <StyledColorPickerDiv>
          <StyledPaletteColorPicker
            itemsSize={30}
            colorItems={colorItems}
            onColorSelect={(newPaletteColor) => {
              setIsCustomColorSelected(false);
              props.onPreferencesChange({widgetColor: lightTheme.palette[newPaletteColor].shade40});
            }}
            paletteColor={paletteColor}
          />
          <StyledCustomColorDiv>
            <PaletteColorPickerItem
              size={30}
              color={
                (tryParseColor(props.edited.preferences.widgetColor) &&
                  props.edited.preferences.widgetColor) ||
                '#000'
              }
              isSelected={
                (!paletteColor || isCustomColorSelected) &&
                Boolean(tryParseColor(props.edited.preferences.widgetColor))
              }
              onClick={() => {
                setIsCustomColorSelected(true);
              }}
            />
            <StyledCustomColorTextBox
              noMargin
              size={VisualSizesEnum.MEDIUM}
              value={props.edited.preferences.widgetColor}
              onChange={(newColor) => {
                props.onPreferencesChange({widgetColor: newColor});
              }}
              hasError={!tryParseColor(props.edited.preferences.widgetColor)}
              errorMessage={
                <Trans id="settingschannel-widgetcolor-error">Please enter a valid hex code</Trans>
              }
            />
          </StyledCustomColorDiv>
        </StyledColorPickerDiv>
      }
      hasVerticalLayout
    />
  );
};
